import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import type { JSX } from "react";

import { ERROR_CODES, SALES_SHARE_MAX_PDF_BYTES, type SalesOrgLeadDetail } from "@xop/dashboard-shared";

import { MultiSelect, type Option } from "../../../components/ui/multi-select.js";
import { toast } from "../../../components/ui/use-toast.js";
import { ApiError } from "../../../lib/api.js";
import { salesApi } from "../client.js";
import { leadPdfFilename } from "./leadPdf.js";

/**
 * Share a generated lead by email, with the lead's PDF attached. The operator
 * picks teammates (sales managers / admins, seeded from the dashboard user
 * directory) and/or types arbitrary addresses, edits the subject + message,
 * and sends.
 *
 * The PDF comes from the `getPdf` prop — the SAME `buildLeadPdf(lead)` vector
 * document the lead page's "Export PDF" download uses — so the emailed
 * attachment is byte-for-byte identical to the export. It's uploaded base64;
 * the server attaches it and sends via the shared Mailer. (A small size guard
 * below keeps the body under the server's limit; vector PDFs are tiny so it
 * only ever trips on a pathological input.) `getPdf` lives in LeadPage.
 *
 * This is a plain form Dialog (NOT `ConfirmDialog`) — sharing isn't a
 * destructive action; it's a create-and-send form.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Split a free-text field ("a@x.com, b@y.com") into addresses. */
function parseEmails(raw: string): { valid: string[]; invalid: string[] } {
  const tokens = raw
    .split(/[,;\s]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
  const valid: string[] = [];
  const invalid: string[] = [];
  for (const t of tokens) {
    if (EMAIL_RE.test(t)) valid.push(t);
    else invalid.push(t);
  }
  return { valid, invalid };
}

/** Read a Blob as base64 (no data-URI prefix). */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result ?? "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read PDF"));
    reader.readAsDataURL(blob);
  });
}

function uniq(emails: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const e of emails) {
    const key = e.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(e);
  }
  return out;
}

export interface ShareLeadDialogProps {
  open: boolean;
  onClose: () => void;
  lead: SalesOrgLeadDetail;
  /** Produce the PDF to attach — the same `buildLeadPdf(lead)` vector document
   * the Export button downloads. Async to keep the call site flexible. */
  getPdf: () => Promise<Blob>;
}

export function ShareLeadDialog({ open, onClose, lead, getPdf }: ShareLeadDialogProps): JSX.Element {
  const [selected, setSelected] = useState<string[]>([]);
  const [customRaw, setCustomRaw] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  // Reset the form each time the dialog OPENS for a lead. Keyed on `lead.id`,
  // NOT the whole `lead` object: the lead page refetches on an interval, so a
  // new object identity would otherwise wipe the operator's typed recipients /
  // message mid-edit. id is stable across refetches.
  useEffect(() => {
    if (!open) return;
    setSubject(`XOP lead: ${lead.name}`);
    setMessage(
      `Sharing the lead brief for ${lead.name}. The full brief is attached as a PDF.`,
    );
    setSelected([]);
    setCustomRaw("");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally not on `lead`
  }, [open, lead.id]);

  const recipients = useQuery({
    queryKey: ["sales", "share", "recipients"],
    queryFn: () => salesApi.listShareRecipients(),
    enabled: open,
    staleTime: 60_000,
  });

  const options: Option[] = useMemo(
    () =>
      (recipients.data?.recipients ?? []).map((r) => ({
        label: r.role === "super_admin" ? `${r.email} (admin)` : r.email,
        value: r.email,
      })),
    [recipients.data],
  );

  const custom = useMemo(() => parseEmails(customRaw), [customRaw]);
  const allRecipients = useMemo(() => uniq([...selected, ...custom.valid]), [selected, custom.valid]);
  const filename = leadPdfFilename(lead);

  const mut = useMutation({
    mutationFn: async () => {
      const pdf = await getPdf();
      // Guard the size BEFORE uploading. The share route rejects an over-limit
      // body by resetting the connection mid-upload, which the browser surfaces
      // as a bare "Load failed" with no typed code — so a too-big brief would
      // otherwise show that opaque error. Pre-checking against the SAME ceiling
      // the server enforces (SALES_SHARE_MAX_PDF_BYTES) keeps every real upload
      // under the body limit and lets us show an actionable message instead.
      if (pdf.size > SALES_SHARE_MAX_PDF_BYTES) {
        const mb = (pdf.size / (1024 * 1024)).toFixed(1);
        const maxMb = Math.round(SALES_SHARE_MAX_PDF_BYTES / (1024 * 1024));
        throw new Error(
          `This lead brief is too large to email (${mb} MB; the limit is ${maxMb} MB). ` +
            `Use “Export PDF” to download it instead.`,
        );
      }
      const pdf_base64 = await blobToBase64(pdf);
      return salesApi.shareLead(lead.id, {
        to: allRecipients,
        subject: subject.trim(),
        message: message.trim(),
        lead_name: lead.name,
        filename,
        pdf_base64,
      });
    },
    onSuccess: (res) => {
      toast({
        title: "Lead shared",
        description: `Emailed the brief to ${res.sent_to} recipient${res.sent_to === 1 ? "" : "s"}.`,
        severity: "success",
      });
      onClose();
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : undefined;
      const description =
        code === "SALES_SHARE_MAIL_NOT_CONFIGURED"
          ? "Email isn't configured on the server. Ask an admin to set MAIL_* (Mailjet)."
          : code === ERROR_CODES.PAYLOAD_TOO_LARGE
            ? // The server rejected the upload as over its body limit. On a current
              // build the brief is a tiny vector PDF, so this only fires on a STALE
              // client still generating a multi-MB page screenshot — point the
              // operator at the fix (update the app) and the manual fallback.
              "This lead brief is too large to email. Update XOP Command Center to the latest version, or use “Export PDF” to download and attach it manually."
            : err instanceof Error
              ? err.message
              : "Could not send the email.";
      toast({ title: "Share failed", description, variant: "destructive" });
    },
  });

  const hasInvalid = custom.invalid.length > 0;
  const canSend =
    allRecipients.length > 0 && subject.trim().length > 0 && !hasInvalid && !mut.isPending;

  return (
    <Dialog open={open} onClose={mut.isPending ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Share lead via email</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "grid", gap: 2 }}>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            Generate a PDF brief for <strong>{lead.name}</strong> and email it. Pick
            teammates below or add any email address.
          </Typography>

          <Box sx={{ display: "grid", gap: 0.75 }}>
            <Typography component="label" sx={{ fontSize: 12, fontWeight: 600, color: "text.primary" }}>
              Sales managers &amp; admins
            </Typography>
            <MultiSelect
              options={options}
              selected={selected}
              onChange={setSelected}
              placeholder={
                recipients.isLoading
                  ? "Loading teammates…"
                  : options.length === 0
                    ? "No teammates found — add emails below"
                    : "Select teammates…"
              }
              emptyMessage="No matching teammate."
            />
          </Box>

          <TextField
            label="Other emails"
            placeholder="alice@firm.com, bob@firm.com"
            value={customRaw}
            onChange={(e) => setCustomRaw(e.target.value)}
            fullWidth
            size="small"
            inputProps={{ "data-testid": "share-custom-emails" }}
            {...(hasInvalid
              ? { error: true, helperText: `Not a valid email: ${custom.invalid.join(", ")}` }
              : { helperText: "Comma- or space-separated. Optional." })}
          />

          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            size="small"
            inputProps={{ maxLength: 200 }}
            {...(subject.trim().length === 0 ? { error: true, helperText: "Subject is required." } : {})}
          />

          <TextField
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            size="small"
            inputProps={{ maxLength: 5000 }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              bgcolor: "surfaceSoft",
              px: 1.5,
              py: 1,
              fontSize: 13,
              color: "text.secondary",
            }}
          >
            <Box component="span" aria-hidden>📎</Box>
            <Box component="span" sx={{ fontFamily: "ui-monospace, monospace", color: "text.primary" }}>
              {filename}
            </Box>
            <Box component="span" sx={{ ml: "auto", fontSize: 12 }}>
              {allRecipients.length === 0
                ? "No recipients yet"
                : `Will send to ${allRecipients.length} recipient${allRecipients.length === 1 ? "" : "s"}`}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mut.isPending} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={() => mut.mutate()}
          disabled={!canSend}
          variant="contained"
          data-testid="share-send"
          startIcon={mut.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {mut.isPending ? "Sending…" : "Send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
